import { Box, Button, Grid, Link, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import TextField from '@/components/textField';
import ErrorMessage from '@/components/errorMessage';
import { useApiContext } from '@/contexts/apiContext';
import { isEmail } from 'validator';

const ForgotPasswordPage = () => {
  const apiContext = useApiContext();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [apiError, setApiError] = useState<any>();
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setApiError(null);
    const errors: any = {};
    let hasError = false;

    if (!executeRecaptcha) {
      setApiError('ReCAPTCHA not ready. Please try again.');
      return;
    }

    try {
      if (!email) {
        errors['email'] = 'Required';
        hasError = true;
      }
      if (!isEmail(email)) {
        errors['email'] = 'Please enter valid email';
        hasError = true;
      }
      if (!hasError) {
        setLoading(true);
        const recaptchaToken = await executeRecaptcha('forgot_password');
        await apiContext.forgotPassword({ recaptchaToken, email });
        setSubmitted(true);
      }
    } catch (error: any) {
      setApiError(error);
    }
    setErrors(errors);
    setLoading(false);
  };

  const handleCancel = () => navigate('/');

  return (
    <Grid container justifyContent="center" alignItems={'flex-start'} spacing={4}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: '12px',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Header */}
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Forgot password
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Enter your email to receive a reset link
            </Typography>
            {apiError && (
              <Box sx={{ mt: 1 }}>
                <ErrorMessage error={apiError} />
              </Box>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ px: 3, py: 2.5 }}>
            {!submitted && (
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    value={email}
                    label={'Email address'}
                    required
                    error={errors['email']}
                    onChange={setEmail}
                    type="email"
                    maxLength={100}
                  />
                </Grid>
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <Link href="https://policies.google.com/privacy" target="_blank">
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link href="https://policies.google.com/terms" target="_blank">
                      Terms of Service
                    </Link>{' '}
                    apply.
                  </Typography>
                </Grid>
              </Grid>
            )}
            {submitted && (
              <Typography variant="body2" color="text.secondary">
                {`If a user exists with email ${email}, an email will be sent with a link to reset the password. The link will be valid for 60 minutes.`}
              </Typography>
            )}
          </Box>

          {/* Action bar */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button variant="text" size="small" onClick={handleCancel} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {submitted ? 'Close' : 'Cancel'}
            </Button>
            {!submitted && email && (
              <Button
                loading={loading}
                size="small"
                color="secondary"
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                  color: 'text.primary',
                }}
              >
                Submit
              </Button>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ForgotPasswordPage;
